import React from 'react';
import { Rocket, Gauge, Footprints } from 'lucide-react';
import {
  FFB_PRESETS,
  PEDAL_PROFILES,
  bookingDurationMinutes,
  bookingReference,
  effectiveRigStatus,
  sessionRemainingMs,
} from '@/lib/kiosk';

/** Why a bay can't be dispatched to right now, shown after its name. */
function unavailableReason(rig, feeds) {
  const status = effectiveRigStatus(rig, feeds);
  if (status === 'LIVE') {
    return `LIVE - ${Math.max(0, Math.ceil(sessionRemainingMs(rig) / 60000))}m left`;
  }
  if (status === 'IN_USE') return 'In Use - car on track';
  if (status === 'CLEANING_REQUIRED') return 'Cleaning Required';
  if (status === 'OUT_OF_SERVICE') return 'Out of Service';
  return status.replace(/_/g, ' ');
}

function Chip({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 border px-3 py-3.5 text-xs font-bold tracking-widest transition-colors ${
        active ? 'border-primary bg-primary text-white' : 'border-white/15 bg-black/30 text-white/70 hover:border-white/30'
      }`}
    >
      {children}
    </button>
  );
}

export default function DispatchConsole({
  booking,
  bays,
  selectedRigId,
  onRigChange,
  ffb,
  onFfb,
  pedal,
  onPedal,
  onDispatch,
  dispatching,
  feeds = [],
}) {
  const canDispatch = !!booking && !!selectedRigId && !dispatching;
  const available = bays.filter((r) => effectiveRigStatus(r, feeds) === 'READY');
  const unavailable = bays.filter((r) => effectiveRigStatus(r, feeds) !== 'READY');

  return (
    <div className="border border-white/10 bg-[#111115] p-5">
      <h2 className="text-white font-black text-base uppercase tracking-tight">Active Selection</h2>

      {!booking ? (
        <p className="text-white/30 text-sm mt-4 py-6 text-center border border-dashed border-white/10">
          Select a booking on the left to begin.
        </p>
      ) : (
        <>
          <div className="flex items-center justify-between gap-4 mt-4 border border-white/10 bg-black/30 px-4 py-3">
            <div className="min-w-0">
              <p className="text-white font-bold text-lg leading-tight truncate">{booking.customer_name}</p>
              <p className="text-white/40 text-[11px] font-mono font-bold tracking-wider mt-1">
                {bookingReference(booking)} · {booking.time_slot}
              </p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-white font-black text-2xl leading-none">{bookingDurationMinutes(booking)}</p>
              <p className="text-white/40 text-[10px] font-bold tracking-widest mt-1">MINUTES</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 mt-5">
            <div>
              <label className="flex items-center gap-1.5 text-white/40 text-[10px] font-bold tracking-widest uppercase mb-2">
                <Rocket className="w-3 h-3" /> Target Rig
              </label>
              <select
                value={selectedRigId}
                onChange={(e) => onRigChange(e.target.value)}
                className="w-full bg-black/40 border border-white/15 focus:border-primary/60 text-white text-sm font-bold px-4 py-4 outline-none"
              >
                <option value="">— Select an available bay —</option>
                {available.length === 0 && (
                  <option value="" disabled>
                    No bays available (Check fleet status below)
                  </option>
                )}
                {available.length > 0 && (
                  <optgroup label="Available Bays">
                    {available.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.bay_number} — {r.name}
                      </option>
                    ))}
                  </optgroup>
                )}
                {unavailable.length > 0 && (
                  <optgroup label="Unavailable / Busy">
                    {unavailable.map((r) => (
                      <option key={r.id} value={`unavailable-${r.id}`} disabled>
                        {r.bay_number} — {r.name} ({unavailableReason(r, feeds)})
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>

            <div>
              <label className="flex items-center gap-1.5 text-white/40 text-[10px] font-bold tracking-widest uppercase mb-2">
                <Gauge className="w-3 h-3" /> Force Feedback Preset
              </label>
              <div className="flex gap-2">
                {FFB_PRESETS.map((p) => (
                  <Chip key={p.value} active={ffb === p.value} onClick={() => onFfb(p.value)}>
                    {p.label}
                  </Chip>
                ))}
              </div>
            </div>

            <div>
              <label className="flex items-center gap-1.5 text-white/40 text-[10px] font-bold tracking-widest uppercase mb-2">
                <Footprints className="w-3 h-3" /> Pedal Profile
              </label>
              <div className="flex gap-2">
                {PEDAL_PROFILES.map((p) => (
                  <Chip key={p.value} active={pedal === p.value} onClick={() => onPedal(p.value)}>
                    {p.label}
                  </Chip>
                ))}
              </div>
            </div>
          </div>

          <button
            onClick={onDispatch}
            disabled={!canDispatch}
            className="w-full mt-5 bg-primary hover:bg-primary/90 disabled:bg-white/5 disabled:text-white/25 text-white font-black text-sm tracking-widest py-5 transition-colors"
          >
            {dispatching ? 'DISPATCHING…' : 'DISPATCH SESSION TO RIG'}
          </button>
        </>
      )}
    </div>
  );
}